/**
 * Причина списания техники — справочник, а не свободный текст (AK-764, Р22).
 *
 * Ровно тот же приём, что у архива полей (`archiveReason.ts`, AK-746): причина
 * едет двумя частями — КОД из списка (по нему потом группируют выбытие) и
 * свободный комментарий (по нему понимают конкретный случай), а в
 * `company_machinery.archive_reason` они складываются одной строкой
 * «код: комментарий». Свой словарь, а не общий с полями: у поля выбытие — это
 * «дубль импорта» и «вернули арендодателю», у машины — «продали» и
 * «разукомплектовали». Общий список был бы списком, в котором половина
 * пунктов невозможна, — а такой список человек перестаёт читать.
 */

/** Коды причин. Порядок — тот, в котором они стоят в списке. */
export const WRITE_OFF_REASON_CODES = [
  'sold',
  'worn_out',
  'stripped',
  'lease_returned',
  'duplicate',
  'input_error',
] as const;

export type WriteOffReasonCode = (typeof WRITE_OFF_REASON_CODES)[number];

/** Подписи — языком инженера, без внутренних терминов. */
export const WRITE_OFF_REASON_LABELS: Record<WriteOffReasonCode, string> = {
  sold: 'Продана',
  worn_out: 'Отслужила срок, утилизирована',
  stripped: 'Разукомплектована на запчасти',
  lease_returned: 'Возвращена арендодателю',
  duplicate: 'Дубль — та же машина уже заведена',
  input_error: 'Ошибка ввода',
};

/** Короткая подпись для строки списанной машины в таблице. */
export const WRITE_OFF_REASON_SHORT: Record<WriteOffReasonCode, string> = {
  sold: 'Продана',
  worn_out: 'Утилизирована',
  stripped: 'На запчасти',
  lease_returned: 'Возврат аренды',
  duplicate: 'Дубль',
  input_error: 'Ошибка ввода',
};

/**
 * Разбор ввода диалога — ТРИ исхода, а не два (канон «Ввод: пусто ≠ мусор»).
 *
 * Различие живёт в типе: пока функция возвращала бы `string | null`, «код не
 * выбран» и «всё хорошо, комментария нет» были бы одним значением, и пустая
 * причина уехала бы на сервер под зелёным тостом «Списано».
 *
 * Комментарий необязателен: код уже отвечает на «почему», а требовать текст на
 * каждой из десяти сеялок — шлагбаум, а не забота.
 */
export function parseWriteOffReason(
  code: WriteOffReasonCode | null,
  comment: string,
): { ok: true; value: string } | { ok: false; error: string } {
  if (!code) {
    return {
      ok: false,
      error: 'Выберите причину — по ней потом видно, куда делась машина',
    };
  }
  const trimmed = comment.trim();
  return { ok: true, value: trimmed ? `${code}: ${trimmed}` : code };
}

/**
 * Обратный разбор строки из БД для показа в таблице.
 *
 * Списания до AK-764 причины не знают вовсе, а строка могла прийти и из
 * будущего словаря — такие показываем как есть, а не выдумываем им код:
 * «Продана» напротив машины, про которую мы этого не знаем, хуже прочерка.
 */
export function splitWriteOffReason(raw: string | null | undefined): {
  code: WriteOffReasonCode | null;
  label: string;
  comment: string;
} {
  const value = (raw ?? '').trim();
  if (!value) return { code: null, label: '', comment: '' };
  const [head, ...rest] = value.split(':');
  const code = WRITE_OFF_REASON_CODES.find((c) => c === head.trim());
  if (!code) return { code: null, label: value, comment: '' };
  return {
    code,
    label: WRITE_OFF_REASON_SHORT[code],
    comment: rest.join(':').trim(),
  };
}
